import React from "react";
import { FileText, Eye, CheckCircle, PenTool, Zap } from "lucide-react";

const LIFECYCLE_STAGES = [
  { key: "draft", label: "Draft", icon: FileText },
  { key: "review", label: "Review", icon: Eye },
  { key: "approved", label: "Approved", icon: CheckCircle },
  { key: "signing", label: "Signing", icon: PenTool },
  { key: "active", label: "Active", icon: Zap },
];

function resolveStageIndex(contract, workflow) {
  const status = contract?.status;
  const sigStatus = contract?.signature_status;
  const psStatus = contract?.print_sign_status;

  if (status === "active" || sigStatus === "fully_executed") return 4;
  if (sigStatus === "awaiting_internal" || sigStatus === "awaiting_counter_signature" || psStatus === "pdf_generated") return 3;
  if (status === "approved") return 2;
  if (status === "under_review" || status === "pending_approval" || status === "supplier_review") return 1;
  if (status === "expired" || status === "terminated") return 4; // terminal states — show full bar completed
  return 0; // draft
}

export function LifecycleProgressBar({ contract, workflow }) {
  const currentIdx = resolveStageIndex(contract, workflow);

  return (
    <div className="flex items-center w-full" data-testid="lifecycle-progress-bar">
      {LIFECYCLE_STAGES.map((stage, idx) => {
        const Icon = stage.icon;
        const isComplete = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isFuture = idx > currentIdx;

        return (
          <React.Fragment key={stage.key}>
            {/* Step circle + label */}
            <div className="flex flex-col items-center relative" style={{ minWidth: 72 }}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isComplete
                    ? "bg-emerald-500 text-white"
                    : isCurrent
                    ? "bg-cyan-600 text-white ring-[3px] ring-cyan-200"
                    : "bg-slate-100 text-slate-400"
                }`}
                data-testid={`lifecycle-stage-${stage.key}`}
              >
                {isComplete ? (
                  <CheckCircle size={14} strokeWidth={2.5} />
                ) : (
                  <Icon size={14} strokeWidth={isCurrent ? 2.5 : 1.5} />
                )}
              </div>
              <span
                className={`text-[10px] mt-1.5 font-medium tracking-wide ${
                  isComplete
                    ? "text-emerald-600"
                    : isCurrent
                    ? "text-cyan-700"
                    : "text-slate-400"
                }`}
              >
                {stage.label}
              </span>
            </div>

            {/* Connector line */}
            {idx < LIFECYCLE_STAGES.length - 1 && (
              <div className="flex-1 h-0.5 mx-1 mt-[-12px] self-start" style={{ marginTop: 15 }}>
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    idx < currentIdx ? "bg-emerald-400" : "bg-slate-200"
                  }`}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
